import React, { useEffect, useState } from "react";
import apiRequest from "../../utils/apiClient";
import { shareSrtUrl } from "../constants";
import { ASK_AI_APPS, ASK_AI_TEXT as T } from "./askAiText";

/**
 * Shared ET, ?ask=1 (the notetaker email's "Ask ChatGPT about this call"): one button copies
 * the call — the Categories chunks as subtitles, short enough to paste whole — with a line
 * telling the AI what it is; then links to ChatGPT / Claude / Gemini. The text is fetched up
 * front so the click can copy straight away (browsers only allow copying inside a click).
 */
const AskAiPanel = ({ onClose, page = false }) => {
  const [text, setText] = useState("");
  const [state, setState] = useState("loading"); // loading | ready | copied | failed

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest(`${shareSrtUrl}?level=categories`, { method: "GET" });
        if (!res.ok) throw new Error(String(res.status));
        let srt = await res.text();
        if (!srt.trim()) {
          // A short call can have no Categories chunks yet: copy it sentence by sentence instead.
          const bySentence = await apiRequest(shareSrtUrl, { method: "GET" });
          if (!bySentence.ok) throw new Error(String(bySentence.status));
          srt = await bySentence.text();
        }
        if (!cancelled) {
          setText(T.PROMPT + srt);
          setState("ready");
        }
      } catch (e) {
        if (!cancelled) setState("failed");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch (e) {
      setState("failed");
    }
  };

  const label = { loading: T.COPYING, ready: T.COPY, copied: T.COPIED, failed: T.COPY_FAILED }[state];
  const panel = (
    <div role="dialog" aria-label={T.TITLE} style={{
      ...(page ? { position: "relative" } : { position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)", zIndex: 2000 }),
      width: "min(440px, calc(100vw - 32px))", boxSizing: "border-box", padding: "18px 20px",
      background: "#17161d", color: "#fff", border: "1px solid #333", borderRadius: 14,
      boxShadow: "0 12px 40px rgba(0,0,0,.5)", fontFamily: "Outfit, sans-serif",
    }}>
      {!page && <button type="button" onClick={onClose} aria-label={T.CLOSE} style={{
        position: "absolute", top: 8, right: 10, background: "none", border: "none", color: "#999",
        fontSize: 20, cursor: "pointer",
      }}>×</button>}
      <div style={{ fontSize: 17, fontWeight: 700, marginRight: 20 }}>{T.TITLE}</div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "#b9b3bd", margin: "6px 0 14px" }}>{T.BODY}</div>
      <button type="button" onClick={copy} disabled={state === "loading" || !text} style={{
        width: "100%", padding: "11px 14px", border: "none", borderRadius: 999, cursor: "pointer",
        background: state === "copied" ? "#2e7d4f" : "#e11d48", color: "#fff", fontSize: 15, fontWeight: 700,
        fontFamily: "inherit", opacity: state === "loading" ? 0.6 : 1,
      }}>{label}</button>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 12, fontSize: 13, color: "#b9b3bd" }}>
        <span>{T.OPEN}</span>
        {ASK_AI_APPS.map((app) => (
          <a key={app.name} href={app.url} target="_blank" rel="noreferrer" style={{
            padding: "6px 12px", border: "1px solid #444", borderRadius: 999, color: "#fff",
            fontWeight: 700, textDecoration: "none",
          }}>{app.name}</a>
        ))}
      </div>
      {page && (
        <button type="button" onClick={onClose} style={{
          marginTop: 16, background: "none", border: "none", padding: 0, color: "#f43f5e",
          fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>{T.OPEN_CLIPS}</button>
      )}
    </div>
  );
  if (!page) return panel;
  return (
    <div style={{
      minHeight: "100vh", background: "#0d0d0d", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 18, padding: 16, boxSizing: "border-box",
    }}>
      <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, fontFamily: "Outfit, sans-serif" }}>🍒 CherryClipz</div>
      {panel}
    </div>
  );
};

export default AskAiPanel;
